import {
  Box,
  Button,
  Typography,
  TextField,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CustomModal from "../../../components/modal/CustomModal";
import { useAuth } from "../../../contexts/UserContext";
import { EconomicIndicatorContext } from "../../../contexts/EconomicIndicatorContext";
import Grid2 from "@mui/material/Unstable_Grid2";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import { useTranslation } from "react-i18next";
import InfoPopper from "../../../components/popper/InfoPopper";
import alertService from "../../../components/alert/alertService";
import { useEffect, useState } from "react";
import {
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { EcoEfficiencyService } from "../../../service/eco-efficiency.service";
import BaseYearSelect from "./BaseYearSelect";
import { common, economic, report } from "../../../i18n";
import { OrganizationBreadcrumbs } from "../../emission/_components/OrganizationBreadcrumbs";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";

type AddEconomicIndicatorFormValues = {
  nameTh: string;
  nameEn: string;
  unit: string;
  isRequireBaseYear: boolean;
  attachment: string;
  components: any[];
};

type Component = {
  _id: string;
  id: number;
  nameTh: string;
  nameEn: string;
  unit: string;
};

const regexEn = /^(?=.*[A-Za-z0-9])[A-Za-z0-9\s\-\_\(\)\.]*$/;

const regexTh = /^[\u0E00-\u0E7F0-9\s\-\_\(\)\.]*$/;

export const AddEconomicIndicator2 = () => {
  const { t } = useTranslation([common, economic, report], {
    nsMode: "fallback",
  });

  const { isAdmin } = useAuth();

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    formState: { isSubmitting },
  } = useForm<AddEconomicIndicatorFormValues>({
    reValidateMode: "onChange",
    mode: "all",
  });

  const isRequireBaseYear = watch("isRequireBaseYear");

  const { organization } = useAuth();

  const {
    modalIsOpen,
    setModalIsOpen,
    setEconomicIndicators,
    setEditData,
    editData,
  } = EconomicIndicatorContext();

  const openModal = () => {
    setEditData(undefined);

    reset();

    setModalIsOpen(true);

    setRows([{ _id: "", id: 1, nameEn: "", nameTh: "", unit: "" }]);
  };

  const closeModal = () => {
    setRows([]);

    setRowModesModel({});

    reset();

    setEditData(undefined);

    setModalIsOpen(false);
  };

  // start data grid

  const apiRef = useGridApiRef();

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const processRowUpdate = async (newRow: Component, oldRow: Component) => {
    const updatedRow = apiRef.current.getRow(newRow.id) as Component;

    // Validate the updated data
    const isValidTh = regexTh.test(updatedRow.nameTh);
    const isValidEn = regexEn.test(updatedRow.nameEn);
    const isValidUnit = regexEn.test(updatedRow.unit);

    if (!isValidTh || !isValidEn || !isValidUnit) {
      if (!isValidTh) {
        alertService.mixin(
          `CI-${updatedRow.id} : ${t("Component")} (${t("Thai")})\n\n${t(
            "Only-Thai-letters-are-allowed",
          )}`,
          "info",
        );
      }
      if (!isValidEn) {
        alertService.mixin(
          `CI-${updatedRow.id} : ${t("Component")} (${t("English")})\n\n${t(
            "Only-English-letters-are-allowed",
          )}`,
          "info",
        );
      }
      if (!isValidUnit) {
        alertService.mixin(
          `CI-${updatedRow.id} : ${t("Unit")}\n\n${t(
            "Only-English-letters-are-allowed",
          )}`,
          "info",
        );
      }
      return oldRow;
    }

    setRows((prevRows) =>
      prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)),
    );

    return updatedRow;
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
      setRowModesModel({
        ...rowModesModel,
        [params.id]: { mode: GridRowModes.View },
      });
    }
  };

  const handleDeleteRow = (id: number) => {
    setRows((prevRows) => {
      const updatedRows = prevRows
        .filter((row) => row.id !== id)
        .map((row, index) => ({
          ...row,
          id: index + 1, // Reindex the remaining rows
        }));
      return updatedRows;
    });
  };

  const handleAddClick = () => {
    if (rows.length >= 5) {
      alertService.mixin(t("Maximum-5-components-allowed"), "info");
      return;
    }
    const id: any = rows.length + 1;
    setRows((oldRows: any) => [
      ...oldRows,
      { id: id, nameEn: "", nameTh: "", unit: "" },
    ]);
  };

  const [rows, setRows] = useState<Component[]>([]);

  const columns: GridColDef<Component>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      width: 80,
      valueFormatter: (value: number) => `CI-${value}`,
    },
    {
      field: "nameEn",
      headerName: `${t("Component")} (${t("English")})`,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const handleBlur = () => {
          const currentValue = params.row.nameEn;
          if (currentValue && !regexEn.test(currentValue)) {
            alertService.mixin(
              `CI-${params.id} : ${t("Component")} (${t("English")})\n\n${t(
                "Only-English-letters-are-allowed",
              )}`,
              "info",
            );
            // Reset the value if invalid
            setRows((prevRows) =>
              prevRows.map((row) =>
                row.id === params.id ? { ...row, nameEn: "" } : row,
              ),
            );
            apiRef.current.updateRows([
              {
                id: params.id,
                nameEn: "",
              },
            ]);
          }
        };
        return (
          <Box sx={{ width: "100%", position: "relative" }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="off"
              onBlur={handleBlur}
              value={params.row.nameEn || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                const isValid = regexEn.test(newValue);
                const errorEl = document.getElementById(
                  `error-text-en-${params.id}`,
                );
                if (errorEl) {
                  errorEl.style.opacity = isValid ? "0" : "1";
                }

                apiRef.current.updateRows([
                  {
                    id: params.id,
                    nameEn: newValue,
                  },
                ]);

                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id ? { ...row, nameEn: newValue } : row,
                  ),
                );
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <FormHelperText
              id={`error-text-en-${params.id}`}
              error
              sx={{
                position: "absolute",
                bottom: -20,
                opacity: 0,
              }}
            >
              {t("English-Only-Allowed")}
            </FormHelperText>
          </Box>
        );
      },
    },
    {
      field: "nameTh",
      headerName: `${t("Component")} (${t("Thai")})`,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const handleBlur = () => {
          const currentValue = params.row.nameTh;
          if (currentValue && !regexTh.test(currentValue)) {
            alertService.mixin(
              `CI-${params.id} : ${t("Component")} (${t("Thai")})\n\n${t(
                "Only-Thai-letters-are-allowed",
              )}`,
              "info",
            );
            // Reset the value if invalid
            setRows((prevRows) =>
              prevRows.map((row) =>
                row.id === params.id ? { ...row, nameTh: "" } : row,
              ),
            );
            apiRef.current.updateRows([
              {
                id: params.id,
                nameTh: "",
              },
            ]);
          }
        };
        return (
          <Box sx={{ width: "100%", position: "relative" }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="off"
              onBlur={handleBlur}
              value={params.row.nameTh || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                const isValid = regexTh.test(newValue);
                const errorEl = document.getElementById(
                  `error-text-${params.id}`,
                );
                if (errorEl) {
                  errorEl.style.opacity = isValid ? "0" : "1";
                }

                apiRef.current.updateRows([
                  {
                    id: params.id,
                    nameTh: newValue,
                  },
                ]);

                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id ? { ...row, nameTh: newValue } : row,
                  ),
                );
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <FormHelperText
              id={`error-text-${params.id}`}
              error
              sx={{
                position: "absolute",
                bottom: -20,
                opacity: 0,
              }}
            >
              {t("Thai-Only-Allowed")}
            </FormHelperText>
          </Box>
        );
      },
    },
    {
      field: "unit",
      headerName: `${t("Unit")} (${t("English")})`,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const handleBlur = () => {
          const currentValue = params.row.unit;
          if (currentValue && !regexEn.test(currentValue)) {
            alertService.mixin(
              `CI-${params.id} : ${t("Unit")}\n\n${t(
                "Only-English-letters-are-allowed",
              )}`,
              "info",
            );
            // Reset the value if invalid
            setRows((prevRows) =>
              prevRows.map((row) =>
                row.id === params.id ? { ...row, unit: "" } : row,
              ),
            );
            apiRef.current.updateRows([
              {
                id: params.id,
                unit: "",
              },
            ]);
          }
        };
        return (
          <Box sx={{ width: "100%", position: "relative" }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="off"
              onBlur={handleBlur}
              value={params.row.unit || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                const isValid = regexEn.test(newValue);
                const errorEl = document.getElementById(
                  `error-text-unit-${params.id}`,
                );
                if (errorEl) {
                  errorEl.style.opacity = isValid ? "0" : "1";
                }

                apiRef.current.updateRows([
                  {
                    id: params.id,
                    unit: newValue,
                  },
                ]);

                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id ? { ...row, unit: newValue } : row,
                  ),
                );
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <FormHelperText
              id={`error-text-unit-${params.id}`}
              error
              sx={{
                position: "absolute",
                bottom: -20,
                opacity: 0,
              }}
            >
              {t("English-Only-Allowed")}
            </FormHelperText>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 80,
      renderCell: (params) => (
        <Button
          variant="text"
          color="error"
          onClick={() => handleDeleteRow(params.row.id)}
          sx={{
            minWidth: "auto",
            padding: "6px",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          <img
            src="/img/delete.svg"
            alt="delete"
            height={"18px"}
            width={"18px"}
            color="error"
          />
        </Button>
      ),
    },
  ];

  const OnSubmit = async (data: AddEconomicIndicatorFormValues) => {
    if (apiRef.current) {
      // Check if there are any rows
      if (rows.length === 0) {
        alertService.mixin(t("Economic-Indicator-Required"), "error");
        return;
      }

      // Validate all rows for completeness and regex patterns
      const incompleteRows: number[] = [];
      const invalidThRows: number[] = [];
      const invalidEnRows: number[] = [];
      const invalidUnitRows: number[] = [];

      rows.forEach((row) => {
        if (!row.nameEn.trim() || !row.nameTh.trim() || !row.unit.trim()) {
          incompleteRows.push(row.id);
        } else {
          // Only check regex if fields are not empty
          if (!regexTh.test(row.nameTh.trim())) {
            invalidThRows.push(row.id);
          }
          if (!regexEn.test(row.nameEn.trim())) {
            invalidEnRows.push(row.id);
          }
          if (!regexEn.test(row.unit.trim())) {
            invalidUnitRows.push(row.id);
          }
        }
      });

      // // Show validation errors if any
      if (incompleteRows.length > 0) {
        alertService.mixin(
          `${t(
            "Please-complete-all-fields-for-components",
          )}: CI-${incompleteRows.join(", CI-")}`,
          "error",
        );
        return;
      }

      // If all validations pass, filter and prepare data
      const validComponents = rows.map((r, index) =>
        editData === undefined
          ? {
              nameEn: r.nameEn.trim(),
              nameTh: r.nameTh.trim(),
              unit: r.unit.trim(),
            }
          : {
              componentRef: r._id,
              nameEn: r.nameEn.trim(),
              nameTh: r.nameTh.trim(),
              unit: r.unit.trim(),
            },
      );

      data.components = validComponents;

      if (!data.isRequireBaseYear) {
        data.unit = `${data.unit} per kgCO₂e`;
      }

      let result;
      try {
        if (editData === undefined) {
          // add mode
          result = await EcoEfficiencyService.createEconomicIndicators(data);
        } else {
          // edit mode

          let temp: any = data;

          if (
            JSON.stringify(editData.components) ===
            JSON.stringify(data.components)
          ) {
            const { components, ...filteredData } = temp;

            temp = filteredData;
          }

          if (data.isRequireBaseYear === editData.isRequireBaseYear) {
            const { isRequireBaseYear, ...filteredData } = temp;
            temp = filteredData;
          }

          result = await EcoEfficiencyService.editEconomicIndicators(
            editData._id,
            temp,
          );
        }

        if (result) {
          setEconomicIndicators(result);
          alertService.mixin(t("Create-Economic-Indicator-Success"));
          closeModal();
        }
      } catch (error) {
        // alertService.mixin(t("Error-creating-economic-indicator"), "error");
      }
    }
  };

  const OnError = (errors: any) => {
    const inputError: string[] = Object.keys(errors);

    const form: HTMLElement | null = document.getElementById(inputError[0]);

    form!.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  };

  // end data grid

  // start img formula

  const [currentSrc, setCurrentSrc] = useState(
    "/img/economic-indicator/with-out-base-year-formula.png",
  );
  const [fading, setFading] = useState(false);

  // end img formula

  useEffect(() => {
    // สำหรับโหมด edit
    if (editData) {
      setValue("nameEn", editData.nameEn);

      setValue("nameTh", editData.nameTh);

      setValue("unit", editData.unit);

      setValue("isRequireBaseYear", editData.isRequireBaseYear);

      setValue("attachment", editData.attachment ?? "");

      const components = editData.components.map((c, index) => ({
        _id: c._id ?? "",
        id: index + 1,
        nameEn: c.nameEn,
        nameTh: c.nameTh,
        unit: c.unit,
      }));

      setValue("components", components);

      setRows(components);
    }
  }, [editData]);

  useEffect(() => {
    setFading(true);
    setTimeout(() => {
      setCurrentSrc(
        isRequireBaseYear
          ? "/img/economic-indicator/base-year-formula.png"
          : "/img/economic-indicator/with-out-base-year-formula.png",
      );
      setFading(false);
    }, 300);

    if (isRequireBaseYear) {
      clearErrors("unit");
      setValue("unit", "unit");
    }
  }, [isRequireBaseYear]);

  return (
    <>
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        startIcon={<img src="/img/add-circle.svg" alt="add-circle" />}
        onClick={openModal}
        disabled={!isAdmin()}
      >
        {t("Add-Economic-Indicator")}
      </Button>

      <CustomModal
        header={
          <Typography variant="h1">
            {editData
              ? t("Edit-Economic-Indicator")
              : t("Add-Economic-Indicator")}
          </Typography>
        }
        open={modalIsOpen}
        onClose={closeModal}
        body={
          <>
            <Box marginY={"20px"}>
              <OrganizationBreadcrumbs
                organization={organization?.name ?? ""}
                location={""}
                subOrganizations={""}
              />
            </Box>

            <form onSubmit={handleSubmit(OnSubmit, OnError)} id="form">
              <Grid2
                container
                columnSpacing={{ xs: "10px", md: "40px" }}
                rowSpacing={"20px"}
                maxWidth={"100%"}
                paddingTop={"24px"}
                disableEqualOverflow
              >
                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    required
                    label={t("Indicator-Name-En")}
                    placeholder={t("Indicator-Name-En")}
                    info={`${t("Indicator-Name")} (${t("English")})`}
                    name="nameEn"
                    control={control}
                    rules={{
                      required: t("input-reqired-error", {
                        param: t("Indicator-Name-En"),
                      }),
                      pattern: {
                        value: regexEn,
                        message: t("Only-English-letters-are-allowed"),
                      },
                    }}
                    defaultValue=""
                  />
                </Grid2>

                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    required
                    label={t("Indicator-Name-Th")}
                    placeholder={t("Indicator-Name-Th")}
                    info={`${t("Indicator-Name")} (${t("Thai")})`}
                    name="nameTh"
                    control={control}
                    rules={{
                      required: t("input-reqired-error", {
                        param: t("Indicator-Name-Th"),
                      }),
                      pattern: {
                        value: regexTh,
                        message: t("Only-Thai-letters-are-allowed"),
                      },
                    }}
                    defaultValue=""
                  />
                </Grid2>

                <Grid2 xs={12} md={6}>
                  <BaseYearSelect
                    control={control}
                    t={t}
                    getValues={getValues}
                  />
                </Grid2>

                <Grid2 xs={12} md={6}>
                  <InputReactHookForm
                    required
                    label={`${t("Unit")} (${t("English")})`}
                    placeholder={t("Unit")}
                    name="unit"
                    control={control}
                    rules={{
                      required: t("input-reqired-error", {
                        param: t("Unit"),
                      }),
                      pattern: {
                        value: regexEn,
                        message: t("Only-English-letters-are-allowed"),
                      },
                    }}
                    info={t("Unit")}
                    endIcon={
                      isRequireBaseYear ? undefined : (
                        <Typography variant="text1">per kgCO₂e</Typography>
                      )
                    }
                    defaultValue="unit"
                  />
                </Grid2>

                <Grid2 xs={12}>
                  <Grid2
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent="start"
                    alignItems="start"
                    width="100%"
                  >
                    <Typography
                      variant="h3"
                      color={"var(--dark)"}
                      sx={{ paddingBottom: "20px" }}
                    >
                      {t("Formula")}
                      {<InfoPopper text={t("Formula-Description")} />}
                    </Typography>

                    <Grid2
                      padding={"20px"}
                      gap={"20px"}
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"center"}
                      alignContent={"center"}
                      width="100%"
                      bgcolor={"var(--gray3)"}
                      borderRadius={"8px"}
                      border={"1px solid var(--gray2)"}
                    >
                      <Typography
                        variant="text1"
                        color={"var(--dark)"}
                        sx={{ paddingBottom: "10px" }}
                        dangerouslySetInnerHTML={{
                          __html: isRequireBaseYear
                            ? t("Base-Year-Image-Description")
                            : t("Without-Base-Year-Image-Description"),
                        }}
                      />

                      <img
                        src={currentSrc}
                        style={{
                          maxHeight: "96px",
                          width: "auto",
                          maxWidth: "100%",
                          opacity: fading ? 0 : 1,
                          transition: "opacity 0.3s ease-out",
                        }}
                        alt="formula"
                        aria-haspopup="true"
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 xs={12} id="component">
                  <Typography
                    variant="h3"
                    color={"var(--dark)"}
                    sx={{
                      marginBottom: "8px",
                      borderBlockStart: "1px",
                    }}
                  >
                    {`${t("Component")}`}

                    <span
                      style={{
                        color: "red",
                      }}
                    >
                      {" * "}
                    </span>
                    <InfoPopper text={t("Component-Description")} />
                  </Typography>

                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={"20px"}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid var(--gray2)",
                      padding: "20px",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      startIcon={
                        <img src="/img/add-circle.svg" alt="add-circle" />
                      }
                      sx={{
                        maxWidth: "255px",
                        marginLeft: "auto",
                      }}
                      onClick={handleAddClick}
                    >
                      {t("Add-Component")}
                    </Button>

                    <AppDataGrid
                      apiRef={apiRef}
                      rows={rows}
                      columns={columns}
                      autoHeight
                      rowHeight={80}
                      hideFooter
                      isCellEditable={() => false}
                      isRowSelectable={() => false}
                      onCellKeyDown={(params, event, details) => {
                        if (params.field === "nameTh") {
                          event.stopPropagation();
                          return;
                        }
                      }}
                      sx={{
                        ...tableStyle(),
                      }}
                      rowModesModel={rowModesModel}
                      processRowUpdate={processRowUpdate}
                      onRowEditStop={handleRowEditStop}
                    />
                  </Box>
                </Grid2>

                <Controller
                  name="attachment"
                  defaultValue=""
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <input value={value} onChange={onChange} type="hidden" />
                  )}
                />

                <Grid2 xs={12}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="contained"
                    type="submit"
                    name="add"
                    sx={{
                      marginTop: "48px",
                    }}
                    disabled={isSubmitting}
                  >
                    {editData === undefined ? t("Add") : t("Save")}
                  </Button>
                </Grid2>
              </Grid2>
            </form>
          </>
        }
      />
    </>
  );
};
